import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import {
  CarouselProvider,
  ButtonNext,
  ButtonBack,
  DotGroup,
  Slider,
  Slide,
} from 'pure-react-carousel';

import Image from '../Image';
import Icon from '../Icon';
import Chevron from '../../svg/icons/ic_chevron-left_20x20.svg';
import HeavyChevron from '../../svg/icons/chevron-left.svg';
import { getTranslation } from '../../providers/TranslationProvider';

import './Carousel.scss';

import { EVENT_IMAGE, VENUE_IMAGE } from './constants';

const Carousel = ({
  buttonPosition,
  buttonRadiusHidden,
  buttonStyles,
  carouselId,
  handleSlideClick,
  hasButtons,
  hasDotGroup,
  ImageServiceConfig,
  infinite,
  isButtonDisabled,
  isIntrinsicHeight,
  isMobile,
  onArrowClick,
  preventVerticalScrollOnTouch,
  slideContent,
  slideType,
  step,
  touchEnabled,
  venueId,
  visibleSlides,
}) => {
  const [shouldShowButtons, setShouldShowButtons] = useState(false);
  let chevron = Chevron;

  const showButtons = () => {
    setShouldShowButtons(true);
  };

  const hideButtons = () => {
    setShouldShowButtons(false);
  };

  const renderButtons = () => {
    if (carouselId === 'collection') {
      chevron = HeavyChevron;
    }

    if (!infinite) {
      isButtonDisabled = null;
    }

    const buttonClassNames = {
      [`Button--circle Carousel__button Carousel__button--${buttonPosition}`]: true,
      'Carousel__button--displayed': shouldShowButtons,
      'Carousel__button--no-border': buttonRadiusHidden,
    };

    return (
      <div
        data-testid="Carousel__button-wrapper"
        style={buttonStyles}
        className={cx(`Carousel__button-wrapper Carousel__button-wrapper--${buttonPosition}`)}
      >
        <ButtonBack
          onClick={() => onArrowClick('previous')}
          disabled={isButtonDisabled}
          aria-label={getTranslation({
            translationKey: 'carousel_previous_label',
            fallback: 'Previous',
          })}
          className={cx('Carousel__button--back ', buttonClassNames)}
        >
          <Icon
            className="Carousel__button-icon"
            component={chevron}
            name="Chevron"
          />
        </ButtonBack>
        <ButtonNext
          onClick={() => onArrowClick('next')}
          disabled={isButtonDisabled}
          aria-label={getTranslation({
            translationKey: 'carousel_next_label',
            fallback: 'Next',
          })}
          className={cx('Carousel__button--next ', buttonClassNames)}
        >
          <Icon
            className="Carousel__button-icon"
            component={chevron}
            name="Chevron"
          />
        </ButtonNext>
      </div>
    );
  };

  const renderDotGroup = () => (
    <div
      data-testid="Carousel__dot-group-outer-container"
      className="Carousel__dot-group-outer-container"
    >
      <div className="Carousel__dot-group-container">
        <DotGroup />
      </div>
    </div>
  );

  const renderSlide = (content, index) => {
    if (slideType === EVENT_IMAGE) {
      return (
        <Image
          data-testid="Carousel__image"
          className={`Carousel__${slideType}`}
          src={content.url}
        />
      );
    }

    if (content.type === VENUE_IMAGE) {
      return (
        <Image
          data-testid="Carousel__image"
          imageId={content.image.id}
          venueId={venueId}
          baseUrl={ImageServiceConfig.baseRequestUrl}
          alt={`content-${index}`}
          className={`Carousel__${content.type}`}
          width="800"
          key={content.id}
        />
      );
    }

    return content.card;
  };

  const getAriaLabel = (slide, index) => {
    const ariaString = `slide ${index + 1} of ${slideContent?.length + 1}`;

    if (slide.label) {
      return `${ariaString}: ${slide.label}`;
    }

    return ariaString;
  };

  return (
    <CarouselProvider
      data-testid="Carousel"
      className={`Carousel ${carouselId}`}
      infinite={infinite}
      isIntrinsicHeight={isIntrinsicHeight}
      naturalSlideHeight={isMobile ? 230 : 800}
      naturalSlideWidth={isMobile ? 345 : 800}
      onMouseEnter={showButtons}
      onMouseLeave={hideButtons}
      preventVerticalScrollOnTouch={preventVerticalScrollOnTouch}
      step={step}
      totalSlides={slideContent?.length}
      touchEnabled={touchEnabled}
      visibleSlides={visibleSlides}
    >
      <Slider
        data-testid="Carousel__slider"
        className="Carousel__slider VenueCardGrid"
        classNameTray="Carousel__slider-tray"
      >
        {slideContent?.map((slide, index) => (
          <Slide
            data-testid="Carousel__slide"
            id={`${carouselId}-slide-${index}`}
            className={`Carousel__slide ${carouselId}-slide-${index}`}
            index={index}
            key={slide.id}
            onClick={handleSlideClick}
            aria-label={getAriaLabel(slide, index)}
          >
            {renderSlide(slide, index)}
          </Slide>
        ))}
      </Slider>

      {hasButtons && slideContent?.length > 1 && renderButtons()}
      {hasDotGroup && slideContent?.length > 1 && renderDotGroup()}
    </CarouselProvider>
  );
};

Carousel.propTypes = {
  buttonPosition: PropTypes.string,
  buttonRadiusHidden: PropTypes.bool,
  buttonStyles: PropTypes.shape({}),
  carouselId: PropTypes.string.isRequired,
  handleSlideClick: PropTypes.func,
  hasButtons: PropTypes.bool,
  hasDotGroup: PropTypes.bool,
  ImageServiceConfig: PropTypes.shape({
    baseRequestUrl: PropTypes.string,
  }),
  infinite: PropTypes.bool.isRequired,
  isButtonDisabled: PropTypes.bool,
  isIntrinsicHeight: PropTypes.bool,
  isMobile: PropTypes.bool,
  onArrowClick: PropTypes.func,
  preventVerticalScrollOnTouch: PropTypes.bool,
  slideContent: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  slideType: PropTypes.string,
  step: PropTypes.number,
  touchEnabled: PropTypes.bool,
  venueId: PropTypes.string,
  visibleSlides: PropTypes.number,
};

Carousel.defaultProps = {
  buttonPosition: null,
  buttonRadiusHidden: false,
  buttonStyles: undefined,
  handleSlideClick: null,
  hasButtons: false,
  hasDotGroup: false,
  ImageServiceConfig: undefined,
  isButtonDisabled: false,
  isIntrinsicHeight: true,
  isMobile: false,
  onArrowClick: () => {},
  preventVerticalScrollOnTouch: true,
  slideType: undefined,
  step: 1,
  touchEnabled: true,
  venueId: null,
  visibleSlides: 1,
};

Carousel.displayName = 'Carousel';
export default Carousel;
